import { useMemo, useState } from "react";
import { SizeOption, SortValue } from "../components/FilterSidebar/FilterSidebar";
import type { ApiShirt } from "../types/Shirt";
import type { Auction } from "../services/auctionService";

/**
 * Herkenbare competitienamen per filter-id. Shirts slaan hun competitie op in
 * `competition` (de Sell-flow vult `league` niet), dus we matchen op beide
 * velden en op de geschreven namen ("La Liga") i.p.v. de interne id's
 * ("laliga") — daardoor had de league-filter voorheen nooit resultaat.
 */
const NAMED_LEAGUE_MATCHERS: Record<string, string[]> = {
  premier: ["premier league", "premier"],
  pro: ["pro league", "jupiler"],
  ligue1: ["ligue 1", "ligue1"],
  eredivisie: ["eredivisie"],
  laliga: ["la liga", "laliga"],
  seriea: ["serie a", "seriea"],
  bundesliga: ["bundesliga"],
  international: ["world cup", "euro", "nations league", "international"],
};

function matchesLeagueFilter(shirt: ApiShirt, selected: Set<string>): boolean {
  const txt = [shirt.league, shirt.competition]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  const matchesNamed = (id: string) =>
    (NAMED_LEAGUE_MATCHERS[id] ?? []).some((name) => txt.includes(name));
  const matchesAnyNamed = Object.keys(NAMED_LEAGUE_MATCHERS).some(matchesNamed);
  return Array.from(selected).some((id) =>
    // "rest" = Rest of World: alles wat bij geen enkele bekende competitie hoort.
    id === "rest" ? !matchesAnyNamed : matchesNamed(id),
  );
}

export function useCatalogFilters(
  items: ApiShirt[],
  selectedLeagues: Set<string>,
  selectedSeason: string,
  auctionMap?: Map<string, Auction>,
  listingType?: string,
): {
  selectedSizes: Set<SizeOption>;
  priceMin: string;
  priceMax: string;
  sortBy: SortValue;
  filteredItems: ApiShirt[];
  toggleSize: (s: SizeOption) => void;
  setPriceMin: (p: string) => void;
  setPriceMax: (p: string) => void;
  setSortBy: (s: SortValue) => void;
  clearFilters: () => void;
} {
  const [selectedSizes, setSelectedSizes] = useState<Set<SizeOption>>(new Set());
  const [priceMin, setPriceMin] = useState<string>("");
  const [priceMax, setPriceMax] = useState<string>("");
  const [sortBy, setSortBy] = useState<SortValue>("relevance");

  const toggleSize = (s: SizeOption) => {
    setSelectedSizes((prev) => {
      const next = new Set(prev);
      next.has(s) ? next.delete(s) : next.add(s);
      return next;
    });
  };

  const clearFilters = () => {
    setSelectedSizes(new Set());
    setPriceMin("");
    setPriceMax("");
    setSortBy("relevance");
  };

  /** Get the effective price for a shirt:
   *  - For auction items (in auctionMap): current_price (live bid) → starting_price
   *  - For all other items: shirt.price → shirt.fixed_price → shirt.auction_starting_price */
  const getPrice = (shirt: ApiShirt): number | null => {
    const auction = auctionMap?.get(shirt.id);
    if (auction) {
      return auction.current_price ?? auction.starting_price ?? null;
    }
    return shirt.price ?? shirt.fixed_price ?? shirt.auction_starting_price ?? null;
  };

  const filteredItems = useMemo<ApiShirt[]>(() => {
    let result = items;

    // Size filter
    if (selectedSizes.size > 0) {
      result = result.filter((shirt) => {
        const size = (shirt.size ?? "").toUpperCase().trim();
        return selectedSizes.has(size as SizeOption);
      });
    }

    // Price filter — uses auction current_price for auction items
    const min = priceMin !== "" ? parseFloat(priceMin) : null;
    const max = priceMax !== "" ? parseFloat(priceMax) : null;
    if (min !== null || max !== null) {
      result = result.filter((shirt) => {
        const price = getPrice(shirt);
        if (price === null) return false;
        if (min !== null && price < min) return false;
        if (max !== null && price > max) return false;
        return true;
      });
    }

    // Season filter — matches e.g. "2023-2024" or "2023"
    if (selectedSeason) {
      result = result.filter((shirt) => {
        const s = shirt.season ?? "";
        return s.startsWith(selectedSeason) || s === selectedSeason;
      });
    }

    // League filter — matcht op competition én league, met echte namen.
    if (selectedLeagues.size > 0) {
      result = result.filter((shirt) => matchesLeagueFilter(shirt, selectedLeagues));
    }

    // Sort — uses auction price for auction items
    if (sortBy === "price-asc") {
      result = [...result].sort((a, b) => (getPrice(a) ?? 0) - (getPrice(b) ?? 0));
    } else if (sortBy === "price-desc") {
      result = [...result].sort((a, b) => (getPrice(b) ?? 0) - (getPrice(a) ?? 0));
    } else if (sortBy === "newest") {
      result = [...result].sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
    }

    // Auction pages: default sort is ending soonest first (ascending end_time)
    const isAuctionView = listingType === "auction" || listingType === "ending-soon";
    if (isAuctionView && sortBy === "relevance") {
      result = [...result].sort((a, b) => {
        const aEnd = auctionMap?.get(a.id)?.end_time ?? a.auction_end_time;
        const bEnd = auctionMap?.get(b.id)?.end_time ?? b.auction_end_time;
        const aTime = aEnd ? new Date(aEnd).getTime() : Infinity;
        const bTime = bEnd ? new Date(bEnd).getTime() : Infinity;
        return aTime - bTime;
      });
    }

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, selectedSizes, priceMin, priceMax, selectedSeason, selectedLeagues, sortBy, auctionMap, listingType]);

  return {
    selectedSizes,
    priceMin,
    priceMax,
    sortBy,
    filteredItems,
    toggleSize,
    setPriceMin,
    setPriceMax,
    setSortBy,
    clearFilters,
  };
}
